import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper, Divider, Stack, TextField } from '@mui/material';
import { useTranslate, useLogout, Loading } from 'react-admin';
import { useTheme } from '@mui/material/styles';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { useDashboardOnClickHandler } from './DashboardOnClickHandler';

const DashboardSection = ({ title, children }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0px 4px 20px rgba(0,0,0,0.06)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          px: 3,
          py: 2,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: 'uppercase',
          textAlign: 'center',
        }}
      >
        {title}
      </Box>
      <Box
        sx={{
          backgroundColor: '#f8f9fb',
          flexGrow: 1,
          p: 3,
        }}
      >
        {children}
      </Box>
    </Paper>
  );
};

const StatCard = ({ title, icon: Icon, children }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 2,
      border: '1px solid #e0e0e0',
      backgroundColor: '#ffffff',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: '1px solid #f0f0f0',
        px: 2.5,
        py: 1.5,
      }}
    >
      {Icon && <Icon sx={{ fontSize: 20, color: '#6b7280' }} />}
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 1, color: '#4b5563' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ flexGrow: 1, p: 2 }}>{children}</Box>
  </Paper>
);

const BigStat = ({ value, label }) => (
  <Box
    sx={{
      textAlign: 'center',
      p: 2,
    }}
  >
    <Typography variant="h3" sx={{ fontWeight: 700, color: '#C41430' }}>
      {value}
    </Typography>
    {label && (
      <Typography variant="caption" sx={{ mt: 1, textTransform: 'uppercase', color: '#6b7280', fontWeight: 600 }}>
        {label}
      </Typography>
    )}
  </Box>
);

const DetailedStatsCard = ({ wadia, nwu, total, totalLabel, onFieldClick, onTotalClick }) => {
  const translate = useTranslate();
  const wadiaLabel = translate('resources.dashboard.sections.labels.wadia', 'Wadia');
  const nwuLabel = translate('resources.dashboard.sections.labels.nwu', 'NWU');

  return (
    <Stack spacing={1.5}>
      <StatRow
        label={wadiaLabel}
        value={wadia}
        muted
        onClick={() => onFieldClick && onFieldClick('wadia')}
        clickable={Boolean(onFieldClick)}
      />
      <StatRow
        label={nwuLabel}
        value={nwu}
        muted
        onClick={() => onFieldClick && onFieldClick('nwu')}
        clickable={Boolean(onFieldClick)}
      />
      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          transition: 'background-color 0.2s ease',
          cursor: onTotalClick ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: onTotalClick ? '#f5f5f5' : 'transparent',
          },
        }}
        onClick={onTotalClick}
      >
        <Typography variant="caption" sx={{ letterSpacing: 1, fontWeight: 700, color: '#9ca3af' }}>
          {totalLabel}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#C41430' }}>
          {total}
        </Typography>
      </Box>
    </Stack>
  );
};

const StatRow = ({ label, value, sublabel, muted = false, onClick, clickable = false }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1,
      px: 1,
      borderBottom: '1px solid #f3f4f6',
      borderRadius: 1,
      transition: 'background-color 0.2s ease',
      cursor: clickable ? 'pointer' : 'default',
      '&:hover': {
        backgroundColor: clickable ? '#f5f5f5' : 'transparent',
      },
      '&:last-of-type': {
        borderBottom: 'none',
      },
    }}
    onClick={clickable ? onClick : undefined}
  >
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: muted ? '#6b7280' : '#374151' }}>
        {label}
      </Typography>
      {sublabel && (
        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
          {sublabel}
        </Typography>
      )}
    </Box>
    <Typography variant="body1" sx={{ fontWeight: 700, color: '#111827' }}>
      {value}
    </Typography>
  </Box>
);

const StatusLegendRow = ({ label, value, color }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1,
      px: 1,
      borderBottom: '1px solid #f3f4f6',
      borderRadius: 1,
      transition: 'background-color 0.2s ease',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
      '&:last-of-type': {
        borderBottom: 'none',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
        {label}
      </Typography>
    </Box>
    <Typography variant="body1" sx={{ fontWeight: 700, color: '#111827' }}>
      {value}
    </Typography>
  </Box>
);

const normalizeEntries = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  if (Array.isArray(payload.data?.results)) {
    return payload.data.results;
  }
  return [];
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const Dashboard = () => {
  const translate = useTranslate();
  const logout = useLogout();
  const { handleFieldClick } = useDashboardOnClickHandler();
  const [latestStats, setLatestStats] = useState(null);
  const [latestDate, setLatestDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  
  // Calculate yesterday's date as default
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };
  
  const [selectedDate, setSelectedDate] = useState(getYesterdayDate());

  const overallLabel = translate('resources.dashboard.sections.labels.ogolnie', 'Ogólnie');
  const sumLabel = translate('resources.dashboard.sections.labels.suma', 'SUMA');

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('access_token');
        
        const params = new URLSearchParams({
          date: selectedDate,
        });
        const response = await fetch(`${API_URL}/dashboard/daily-stats/by-date/?${params.toString()}`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        });

        if (response.status === 401) {
          logout();
          return;
        }
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorBody}`);
        }

        const json = await response.json();

        if (!isMounted) {
          return;
        }

        // by-date endpoint returns object with date and data directly
        setLatestStats(json?.data || null);
        setLatestDate(json?.date || null);
        setFetchError(null);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setFetchError(error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [selectedDate]);

  const oferenciStats = latestStats?.oferenci || {};
  const oferenciErrors = oferenciStats.errors || {};
  const oferenciArchive = oferenciStats.archiwum || {};
  const leadStatuses = oferenciStats.statusy_lead || {};
  const marketingConsents = oferenciStats.zgody_marketingowe || {};
  const isLiveConsents = 'udzielone_zgody_24h' in marketingConsents;
  const consentValue = isLiveConsents
    ? (marketingConsents.udzielone_zgody_24h ?? 0)
    : (marketingConsents.udzielone_zgody ?? 0);
  const consentLabel = isLiveConsents
    ? 'ZGODY UZYSKANE PRZEZ OSTATNIE 24H'
    : `ZGODY UZYSKANE ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('pl-PL')} 00:00 - 23:59`;
  const marketingConsentsData = {
    udzielone_zgody: consentValue,
    nowe_nipy: marketingConsents.nowe_nipy ?? 0,
    istniejacy_oferenci: marketingConsents.istniejacy_oferenci ?? 0,
    wyslane_emaile: marketingConsents.wyslane_emaile ?? 0,
    wyslane_emaile_oferta: marketingConsents.wyslane_emaile_oferta ?? 0,
  };
  const successStats = oferenciStats.sukces || {};
  const successTotals = {
    wadia: successStats.wadia ?? 0,
    nwu: successStats.nwu ?? 0,
    all:
      successStats.all ??
      (successStats.wadia ?? 0) + (successStats.nwu ?? 0),
  };
  const leadStatusData = [
    {
      key: 'blad',
      label: translate('resources.dashboard.sections.oferenci.status_leads.error', 'Z błędem'),
      value: leadStatuses.blad ?? 0,
      color: '#EF4444',
    },
    {
      key: 'blokada_email',
      label: 'z blokadą wysyłki e-mail',
      value: leadStatuses.blokada_email ?? 0,
      color: '#EF4444',
    },
    {
      key: 'klienci_polfund',
      label: 'Klienci Polfund',
      value: leadStatuses.klienci_polfund ?? 0,
      color: '#10B981',
    },
  ];
  const oferenciErrorsData = [
    { key: 'brak_nip', label: 'Brak NIP', value: oferenciErrors.brak_nip ?? 0 },
    { key: 'brak_email', label: 'Brak Email', value: oferenciErrors.brak_email ?? 0 },
    { key: 'brak_pwg', label: 'Brak PWG', value: oferenciErrors.brak_pwg ?? 0 },
    { key: 'nie_znaleziono_w_gus', label: 'Nie znaleziono w GUS', value: oferenciErrors.nie_znaleziono_w_gus ?? 0 },
  ];
  const oferenciErrorsTotal = oferenciErrorsData.reduce((sum, item) => sum + item.value, 0);
  const oferenciArchiveData = [
    { key: 'brak_wadium_nwu', label: 'Brak Wadium/NWU', value: oferenciArchive.brak_wadium_nwu ?? 0 },
    { key: 'niespelnione_pwg', label: 'Niespełnione PWG', value: oferenciArchive.niespelnione_pwg ?? 0 },
    { key: 'niespelniony_gus', label: 'Niespełniony GUS', value: oferenciArchive.niespelniony_gus ?? 0 },
    { key: 'brak_zgody_marketingowej', label: 'Nie uzyskano zgody marketingowej w ciągu 30 dni', value: oferenciArchive.brak_zgody_marketingowej ?? 0 },
  ];
  const oferenciArchiveTotal =
    typeof oferenciArchive === 'object' && oferenciArchive !== null
      ? Object.values(oferenciArchive).reduce(
          (sum, val) => sum + (typeof val === 'number' ? val : 0),
          0
        )
      : oferenciArchiveData.reduce((sum, item) => sum + item.value, 0);

  const zamowieniaStats = latestStats?.zamowienia || {};
  const zamowieniaDownloadedTotals = {
    wadia: zamowieniaStats.pobrane?.wadia ?? 0,
    nwu: zamowieniaStats.pobrane?.nwu ?? 0,
    all:
      zamowieniaStats.pobrane?.all ??
      (zamowieniaStats.pobrane?.wadia ?? 0) +
        (zamowieniaStats.pobrane?.nwu ?? 0),
  };
  const zamowieniaErrorsTotals = {
    wadia: zamowieniaStats.errors?.wadia ?? 0,
    nwu: zamowieniaStats.errors?.nwu ?? 0,
    all:
      zamowieniaStats.errors?.all ??
      (zamowieniaStats.errors?.wadia ?? 0) +
        (zamowieniaStats.errors?.nwu ?? 0),
  };
  const zamowieniaArchivedValue =
    zamowieniaStats.archiwum?.brak_wadium_nwu ?? 0;

  const predykcjaStats = latestStats?.predykcja || {};
  const predictionsOrdersTotals = {
    wadia: predykcjaStats.zamowienia?.wadia ?? 0,
    nwu: predykcjaStats.zamowienia?.nwu ?? 0,
    all:
      predykcjaStats.zamowienia?.all ??
      (predykcjaStats.zamowienia?.wadia ?? 0) +
        (predykcjaStats.zamowienia?.nwu ?? 0),
  };
  const predictionsBiddersTotals = {
    wadia: predykcjaStats.oferenci?.wadia ?? 0,
    nwu: predykcjaStats.oferenci?.nwu ?? 0,
    all:
      predykcjaStats.oferenci?.all ??
      (predykcjaStats.oferenci?.wadia ?? 0) +
        (predykcjaStats.oferenci?.nwu ?? 0),
  };

  const latestDateLabel = latestDate
    ? new Date(latestDate).toLocaleDateString('pl-PL')
    : null;

  if (loading) return <Loading />;

  return (
    <Box
      sx={{
        backgroundColor: '#F5F5F5',
        minHeight: 'calc(100vh - 64px)',
      }}
    >
      <Box>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#333333' }}>
                {translate('resources.dashboard.heading', 'Statystyki')}
              </Typography>
              {latestDateLabel && (
                <Typography variant="body2" sx={{ color: '#666666', mt: 0.5 }}>
                  {translate('resources.dashboard.latest_date', 'Dane z dnia')}: {latestDateLabel}
                </Typography>
              )}
            </Box>
            <TextField
              label={translate('resources.dashboard.select_date', 'Wybierz datę')}
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: getYesterdayDate(), // Prevent selecting future dates
              }}
              sx={{
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                },
              }}
            />
          </Box>
          {fetchError && (
            <Typography variant="body2" sx={{ color: '#b91c1c', mt: 0.5 }}>
              {translate(
                'resources.dashboard.load_error',
                'Nie udało się wczytać najnowszych statystyk. Pokazujemy wartości domyślne.'
              )}
            </Typography>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <DashboardSection title={translate('resources.dashboard.sections.oferenci.title', 'Oferenci')}>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                  <StatCard
                    title="Pozyskane nowe zgody"
                    icon={GroupOutlinedIcon}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BigStat
                        value={marketingConsentsData.udzielone_zgody}
                        label={consentLabel}
                      />
                    </Box>
                  </StatCard>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <StatCard
                    title={translate('resources.dashboard.sections.oferenci.status_leads.title', 'Statusy leadów')}
                    icon={GroupOutlinedIcon}
                  >
                    <StatRow 
                      label="Ilość wysłanych emaili z prośbą o zgodę marketingową" 
                      value={leadStatuses.wyslane_emaile ?? 0} 
                    />
                    <StatRow 
                      label="Ilość wysłanych emaili z ofertą" 
                      value={leadStatuses.wyslane_emaile_oferta ?? 0} 
                    />
                    <StatusLegendRow 
                      label="Klienci Polfund" 
                      value={leadStatuses.klienci_polfund ?? 0} 
                      color="#10B981" 
                    />
                    <StatusLegendRow 
                      label="z blokadą wysyłki e-mail" 
                      value={leadStatuses.blokada_email ?? 0} 
                      color="#EF4444" 
                    />
                    <StatusLegendRow 
                      label={translate('resources.dashboard.sections.oferenci.status_leads.error', 'Z błędem')} 
                      value={leadStatuses.blad ?? 0} 
                      color="#EF4444" 
                    />
                  </StatCard>
                </Grid>

                <Grid item xs={12}>
                  <StatCard
                    title="Statystyki pozyskania i kontaktu z oferentami"
                    icon={GroupOutlinedIcon}
                  >
                    <StatRow 
                      label="Ilość pozyskanych nowych NIP" 
                      value={marketingConsentsData.nowe_nipy} 
                    />
                    <StatRow 
                      label="Ilość wystąpień istniejących oferentów" 
                      value={marketingConsentsData.istniejacy_oferenci} 
                    />
                    <StatRow 
                      label="Ilość wysłanych emaili z prośbą o zgodę marketingową" 
                      value={marketingConsentsData.wyslane_emaile} 
                    />
                    <StatRow 
                      label="Ilość wysłanych emaili z ofertą" 
                      value={marketingConsentsData.wyslane_emaile_oferta} 
                    />
                  </StatCard>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <StatCard
                    title={translate('resources.dashboard.sections.oferenci.validation_errors', 'Błędy walidacji')}
                    icon={ErrorOutlineOutlinedIcon}
                  >
                    {oferenciErrorsData.map((item) => (
                      <StatRow
                        key={item.key}
                        label={item.label}
                        value={item.value}
                        clickable
                        onClick={() => handleFieldClick('oferenci_errors', item.key, false, selectedDate)}
                      />
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                      onClick={() => handleFieldClick('oferenci_errors', undefined, true)}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#9ca3af', letterSpacing: 1 }}>
                        {sumLabel}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#C41430' }}>
                        {oferenciErrorsTotal}
                      </Typography>
                    </Box>
                  </StatCard>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <StatCard
                    title={translate('resources.dashboard.sections.oferenci.archive', 'Archiwum')}
                    icon={ArchiveOutlinedIcon}
                  >
                    {oferenciArchiveData.map((item) => (
                      <StatRow key={item.key} label={item.label} value={item.value} />
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#9ca3af', letterSpacing: 1 }}>
                        {translate('resources.dashboard.sections.labels.wszyscy', 'Wszyscy')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#C41430' }}>
                        {oferenciArchiveTotal}
                      </Typography>
                    </Box>
                  </StatCard>
                </Grid>
              </Grid>
            </DashboardSection>
          </Grid>

          <Grid item xs={12} lg={4}>
            <DashboardSection title={translate('resources.dashboard.sections.zamowienia.title', 'Zamówienia')}>
              <Stack spacing={3}>
                <StatCard
                  title={translate('resources.dashboard.sections.zamowienia.downloaded', 'Pobrane zamówienia')}
                  icon={ShoppingCartOutlinedIcon}
                >
                  <DetailedStatsCard
                    wadia={zamowieniaDownloadedTotals.wadia}
                    nwu={zamowieniaDownloadedTotals.nwu}
                    total={zamowieniaDownloadedTotals.all}
                    totalLabel={overallLabel}
                    onFieldClick={(fieldKey) => handleFieldClick('zamowienia_downloaded', fieldKey)}
                    onTotalClick={() => handleFieldClick('zamowienia_downloaded', undefined, true)}
                  />
                </StatCard>
                <StatCard
                  title={translate('resources.dashboard.sections.zamowienia.errors', 'Przetargi z błędami')}
                  icon={ReportProblemOutlinedIcon}
                >
                  <DetailedStatsCard
                    wadia={zamowieniaErrorsTotals.wadia}
                    nwu={zamowieniaErrorsTotals.nwu}
                    total={zamowieniaErrorsTotals.all}
                    totalLabel={overallLabel}
                    onFieldClick={(fieldKey) => handleFieldClick('zamowienia_errors', fieldKey)}
                    onTotalClick={() => handleFieldClick('zamowienia_errors', undefined, true)}
                  />
                </StatCard>
                <StatCard
                  title={translate('resources.dashboard.sections.zamowienia.archived', 'Archiwizacja')}
                  icon={ArchiveOutlinedIcon}
                >
                  <StatRow label="Brak Wadium / NWU" value={zamowieniaArchivedValue} />
                </StatCard>
              </Stack>
            </DashboardSection>
          </Grid>

          <Grid item xs={12}>
            <DashboardSection title={translate('resources.dashboard.sections.predykcja.title', 'Predykcje')}>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                  <StatCard
                    title={translate('resources.dashboard.sections.predykcja.new_tenders', 'Nowe: Zamówienia')}
                    icon={TrendingUpOutlinedIcon}
                  >
                    <DetailedStatsCard
                      wadia={predictionsOrdersTotals.wadia}
                      nwu={predictionsOrdersTotals.nwu}
                      total={predictionsOrdersTotals.all}
                      totalLabel={overallLabel}
                    />
                  </StatCard>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <StatCard
                    title={translate('resources.dashboard.sections.predykcja.new_bidders', 'Nowe: Oferenci')}
                    icon={PeopleAltOutlinedIcon}
                  >
                    <DetailedStatsCard
                      wadia={predictionsBiddersTotals.wadia}
                      nwu={predictionsBiddersTotals.nwu}
                      total={predictionsBiddersTotals.all}
                      totalLabel={overallLabel}
                    />
                  </StatCard>
                </Grid>
              </Grid>
            </DashboardSection>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;
